import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import './PackageDelivery.css';
import { mapStyles } from './mapStyles';
import { useAuth } from '../../AuthContext';
import {
  nitrologo,
  drivercar,
  locationpickup,
  locationdropoff
} from '../../utilities/images';
import RideProfileDropdown from './RideProfileDropdown';
import Chat from './Chat';
import { db } from '../../firebase';
import { ref, onValue, push, set, get } from "firebase/database";

const mockDeliveries = [
  { id: '1', title: 'Nike Sneakers', trackingId: '#9DJILEKSOA', status: 'delivered', type: 'clothing' },
  { id: '2', title: 'Macbook Pro M2', trackingId: '#9DJILEKSOA', status: 'pending_pickup', type: 'electronics' },
  { id: '3', title: 'Fragile Plates', trackingId: '#9DJILEKSOA', status: 'in_transit', type: 'fragile' },
  { id: '4', title: 'Birthday Gift', trackingId: '#9DJILEKSOA', status: 'failed', type: 'gift' },
  { id: '5', title: 'Small package', trackingId: '#9DJILEKSOA', status: 'failed', type: 'package' },
  { id: '6', title: 'Document', trackingId: '#9DJILEKSOA', status: 'failed', type: 'document' },
  { id: '7', title: 'Groceries', trackingId: '#9DJILEKSOA', status: 'failed', type: 'groceries' },
];

function PackageDelivery() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [userDeliveries, setUserDeliveries] = useState([]);
  const [selectedTrackingTrip, setSelectedTrackingTrip] = useState(null);
  const [assignedCourier, setAssignedCourier] = useState(null);
  
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const profileDropdownRef = useRef(null);

  // Map Refs
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const driverMarkersRef = useRef({});
  const pickupMarkerRef = useRef(null);
  const dropoffMarkerRef = useRef(null);

  // Quick Action Modals
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  
  // Incident report form state
  const [reportReason, setReportReason] = useState('Damaged Package');
  const [reportComments, setReportComments] = useState('');
  const [isReporting, setIsReporting] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);

  // Map zoom handlers
  const handleZoomIn = () => {
    if (!map) return;
    map.setZoom(map.getZoom() + 1);
  };

  const handleZoomOut = () => {
    if (!map) return;
    map.setZoom(map.getZoom() - 1);
  };

  // Google Map Initialization
  useEffect(() => {
    if (!mapRef.current || !window.google) return;

    const initialCenter = { lat: 6.5244, lng: 3.3792 }; // Lagos coordinates
    const mapInstance = new window.google.maps.Map(mapRef.current, {
      center: initialCenter,
      zoom: 13,
      styles: mapStyles,
      disableDefaultUI: true,
    });
    setMap(mapInstance);

    return () => {
      Object.values(driverMarkersRef.current).forEach(marker => marker.setMap(null));
      driverMarkersRef.current = {};
    };
  }, [mapRef]);

  // Live Driver / Courier Markers on Map
  useEffect(() => {
    if (!map || !window.google) return;

    const driverStatesRef = ref(db, 'driverStates');
    const driverPresencesRef = ref(db, 'driverPresence');
    const driverLocationsRef = ref(db, 'driverLocations');

    let allData = { states: {}, presences: {}, locations: {} };

    const updateMarkers = () => {
      const { states, presences, locations } = allData;
      const driverIds = new Set([
        ...Object.keys(states),
        ...Object.keys(presences),
        ...Object.keys(locations)
      ]);

      driverIds.forEach((driverId) => {
        const state = states[driverId];
        const presence = presences[driverId];
        const locationData = locations[driverId];
        const existingMarker = driverMarkersRef.current[driverId];

        const isAvailable = state?.state === 'available' && presence?.online === true;

        if (isAvailable && locationData?.lat && locationData?.lng) {
          const position = { lat: locationData.lat, lng: locationData.lng };

          if (existingMarker) {
            existingMarker.setPosition(position);
          } else {
            const driverMarker = new window.google.maps.Marker({
              position,
              map,
              icon: {
                url: drivercar,
                scaledSize: new window.google.maps.Size(40, 40),
                anchor: new window.google.maps.Point(20, 20),
              },
              title: `Courier ${driverId}`
            });
            driverMarkersRef.current[driverId] = driverMarker;
          }
        } else {
          if (existingMarker) {
            existingMarker.setMap(null);
            delete driverMarkersRef.current[driverId];
          }
        }
      });
    };

    const statesListener = onValue(driverStatesRef, (snapshot) => { allData.states = snapshot.val() || {}; updateMarkers(); });
    const presencesListener = onValue(driverPresencesRef, (snapshot) => { allData.presences = snapshot.val() || {}; updateMarkers(); });
    const locationsListener = onValue(driverLocationsRef, (snapshot) => { allData.locations = snapshot.val() || {}; updateMarkers(); });

    return () => {
      statesListener();
      presencesListener();
      locationsListener();
    };
  }, [map]);

  // Fetch real user deliveries from Firebase Realtime DB
  useEffect(() => {
    if (!auth.currentUser) return;
    const tripsRef = ref(db, 'tripRequests');
    
    const unsubscribe = onValue(tripsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const deliveries = Object.keys(data)
          .map(key => ({ id: key, ...data[key] }))
          .filter(trip => trip.riderId === auth.currentUser.uid && (trip.isDelivery || ['motorcycle', 'truck', 'bicycle'].includes(trip.vehicleType?.toLowerCase())))
          .map(trip => ({
            id: trip.id,
            title: trip.deliveryDetails?.packageDescription || `${trip.vehicleType?.toUpperCase() || 'Courier'} Delivery`,
            trackingId: `#${trip.id.substring(1, 10).toUpperCase()}`,
            status: trip.status === 'searching' ? 'searching' :
                    trip.status === 'completed' ? 'delivered' :
                    ['found', 'driving_to_pickup', 'driver_arrived'].includes(trip.status) ? 'pending_pickup' :
                    ['on_trip', 'driving_to_destination'].includes(trip.status) ? 'in_transit' : 'failed',
            type: trip.deliveryDetails?.itemType?.toLowerCase()?.includes('document') ? 'document' :
                  trip.deliveryDetails?.itemType?.toLowerCase()?.includes('groc') ? 'groceries' :
                  trip.deliveryDetails?.itemType?.toLowerCase()?.includes('fragile') ? 'fragile' :
                  trip.deliveryDetails?.itemType?.toLowerCase()?.includes('electronics') ? 'electronics' : 'package',
            rawTrip: trip
          }));
        setUserDeliveries(deliveries);
      }
    });

    return () => unsubscribe();
  }, [auth.currentUser]);

  // Handle outside profile dropdown click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileDropdownRef]);

  const handleLogout = async () => {
    try {
      setIsProfileDropdownOpen(false);
      await auth.signOut();
      localStorage.removeItem('9troride_address');
      localStorage.removeItem('9troride_locationType');
      navigate('/');
    } catch (error) {
      console.error("Failed to log out:", error);
    }
  };

  const displayDeliveries = [...userDeliveries, ...mockDeliveries];

  const filteredDeliveries = searchQuery.trim()
    ? displayDeliveries.filter(d => 
        d.trackingId.toLowerCase().includes(searchQuery.toLowerCase().trim()) || 
        d.title.toLowerCase().includes(searchQuery.toLowerCase().trim())
      )
    : displayDeliveries;

  const handleSearchClear = () => {
    setSearchQuery('');
    setSelectedTrackingTrip(null);
    if (pickupMarkerRef.current) {
      pickupMarkerRef.current.setMap(null);
      pickupMarkerRef.current = null;
    }
    if (dropoffMarkerRef.current) {
      dropoffMarkerRef.current.setMap(null);
      dropoffMarkerRef.current = null;
    }
  };

  const handleSelectDelivery = (delivery) => {
    setSelectedTrackingTrip(delivery);

    // Fetch live courier or set mock courier
    if (delivery.rawTrip && delivery.rawTrip.driverId) {
      const driverRef = ref(db, `drivers/${delivery.rawTrip.driverId}`);
      get(driverRef).then((snapshot) => {
        if (snapshot.exists()) {
          const driverData = snapshot.val();
          setAssignedCourier({
            name: driverData.name || '9tro Partner Courier',
            phone: driverData.phone || '',
            photoUrl: driverData.photoUrl || '',
            rating: driverData.rating || 4.8,
            vehicle: driverData.vehicle || { make: 'Motorcycle', model: 'Courier', color: 'Black', licensePlate: 'LAG-123AA' }
          });
        } else {
          setAssignedCourier(null);
        }
      }).catch((err) => {
        console.error("Error fetching courier details:", err);
        setAssignedCourier(null);
      });
    } else if (!delivery.rawTrip && ['delivered', 'in_transit', 'pending_pickup'].includes(delivery.status)) {
      setAssignedCourier({
        name: "Samuel Adebayo",
        phone: "+234 812 345 6789",
        photoUrl: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=256",
        rating: 4.9,
        vehicle: {
          make: "Yamaha",
          model: "Crux",
          color: "Red",
          licensePlate: "LAG-889XY"
        }
      });
    } else {
      setAssignedCourier(null);
    }

    // If there is associated trip coordinates, plot them on map
    if (delivery.rawTrip && map && window.google) {
      const trip = delivery.rawTrip;
      
      if (pickupMarkerRef.current) pickupMarkerRef.current.setMap(null);
      if (dropoffMarkerRef.current) dropoffMarkerRef.current.setMap(null);
 
      pickupMarkerRef.current = new window.google.maps.Marker({
        position: { lat: trip.pickup.lat, lng: trip.pickup.lng },
        map: map,
        icon: { url: locationpickup, scaledSize: new window.google.maps.Size(32, 32) }
      });
 
      dropoffMarkerRef.current = new window.google.maps.Marker({
        position: { lat: trip.destination.lat, lng: trip.destination.lng },
        map: map,
        icon: { url: locationdropoff, scaledSize: new window.google.maps.Size(32, 32) }
      });
 
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(pickupMarkerRef.current.getPosition());
      bounds.extend(dropoffMarkerRef.current.getPosition());
      map.fitBounds(bounds);
    }
  };

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    setIsReporting(true);
    try {
      const reportsRef = ref(db, 'deliveryReports');
      const newReportRef = push(reportsRef);
      await set(newReportRef, {
        reportId: newReportRef.key,
        userId: auth.currentUser?.uid || 'guest',
        reason: reportReason,
        comments: reportComments,
        timestamp: Date.now()
      });
      setReportSuccess(true);
      setReportComments('');
      setTimeout(() => {
        setReportSuccess(false);
        setIsReportModalOpen(false);
      }, 2500);
    } catch (err) {
      console.error("Error submitting report:", err);
      alert("Failed to submit report. Please try again.");
    } finally {
      setIsReporting(false);
    }
  };

  const renderItemIcon = (type) => {
    switch (type) {
      case 'document':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
          </svg>
        );
      case 'electronics':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
            <line x1="8" y1="21" x2="16" y2="21"></line>
            <line x1="12" y1="17" x2="12" y2="21"></line>
          </svg>
        );
      case 'fragile':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 3h12v7a6 6 0 0 1-6 6 6 6 0 0 1-6-6V3z"></path>
            <line x1="12" y1="16" x2="12" y2="22"></line>
            <line x1="9" y1="22" x2="15" y2="22"></line>
          </svg>
        );
      case 'gift':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 12 20 22 4 22 4 12"></polyline>
            <rect x="2" y="7" width="20" height="5"></rect>
            <line x1="12" y1="22" x2="12" y2="7"></line>
            <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path>
            <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path>
          </svg>
        );
      case 'groceries':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4H6z"></path>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <path d="M16 10a4 4 0 0 1-8 0"></path>
          </svg>
        );
      case 'clothing':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23z"></path>
          </svg>
        );
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
            <line x1="12" y1="22.08" x2="12" y2="12"></line>
          </svg>
        );
    }
  };

  return (
    <div className="packagedelivery-page-wrapper">
      <header className="packagedelivery-header">
        <div className="packagedelivery-header-left">
          <button onClick={() => navigate('/')} className="packagedelivery-header-back-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
          </button>
          <Link to="/"><img src={nitrologo} alt="9troRide Logo" className="packagedelivery-header-logo" /></Link>
        </div>
        {auth.currentUser ? (
          <div style={{ position: 'relative' }} ref={profileDropdownRef}>
            <button onClick={() => setIsProfileDropdownOpen(prev => !prev)} className="packagedelivery-header-profile-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
              <span>{auth.riderData?.first_name || auth.currentUser.displayName?.split(' ')[0] || 'Profile'}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
            </button>
            {isProfileDropdownOpen && (
              <RideProfileDropdown user={auth.currentUser} rider={auth.riderData} onLogout={handleLogout} />
            )}
          </div>
        ) : (
          <Link to="/ride-login" state={{ from: location }} className="packagedelivery-header-login-btn">
            Log In
          </Link>
        )}
      </header>

      <div className="packagedelivery-page-container">
        {/* Left Sidebar containing Dashboard UI */}
        <div className="packagedelivery-sidebar">
          <div className="delivery-container">
            {/* Track Your Package Card */}
            <div className="track-card">
              <h3>Track your package</h3>
              <div className="track-search-wrapper">
                <svg className="track-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                <input 
                  type="text" 
                  placeholder="Enter tracking ID" 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="track-input"
                />
                {searchQuery && (
                  <button className="track-clear-btn" onClick={handleSearchClear} aria-label="Clear search">
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* Recent Deliveries list */}
            <div className="deliveries-section-header">
              <h2>Recent deliveries</h2>
              <button className="see-all-btn" onClick={() => navigate('/')}>See all</button>
            </div>

            <div className="deliveries-list">
              {filteredDeliveries.length > 0 ? (
                filteredDeliveries.map((delivery) => (
                  <div 
                    key={`${delivery.id}-${delivery.title}`} 
                    className={`delivery-item-card ${delivery.status}`}
                    onClick={() => handleSelectDelivery(delivery)}
                  >
                    <div className={`delivery-item-icon-container ${delivery.type}`}>
                      {renderItemIcon(delivery.type)}
                    </div>
                    <div className="delivery-item-info">
                      <h4>{delivery.title}</h4>
                      <p>Tracking ID: {delivery.trackingId}</p>
                    </div>
                    <span className={`status-badge ${delivery.status}`}>
                      {delivery.status.replace(/_/g, ' ')}
                    </span>
                    <svg className="chevron-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </div>
                ))
              ) : (
                <div className="empty-deliveries">
                  <div className="empty-deliveries-icon">📦</div>
                  <h3>No matching deliveries</h3>
                  <p>Try searching for a different tracking ID or package name.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sibling Details Panel matching the PNG mockup */}
        {selectedTrackingTrip && (
          <div className="packagedelivery-details-panel">
            <div className="packagedelivery-details-header">
              <div className="header-title-group">
                <h2>Delivery details</h2>
                <p>{selectedTrackingTrip.trackingId || '#9DJKLEOIS'}</p>
              </div>
              <button className="packagedelivery-details-back-btn" onClick={() => setSelectedTrackingTrip(null)} aria-label="Close">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            {/* Dark status card */}
            <div className="details-dark-card">
              <div className="dark-card-title-row">
                <span className="dark-card-label">Your package</span>
                <span className="dark-card-id">{selectedTrackingTrip.trackingId || '#9DJKLEOIS'}</span>
              </div>
              <div className="dark-card-badge-row">
                <span className={`dark-badge ${selectedTrackingTrip.status}`}>
                  {selectedTrackingTrip.status === 'pending_pickup' ? 'Pending pickup' : selectedTrackingTrip.status.replace(/_/g, ' ')}
                </span>
              </div>
              
              {/* Progress Slider */}
              <div className="details-progress-slider">
                <div className="slider-left-endpoint completed">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                    <line x1="12" y1="22.08" x2="12" y2="12"></line>
                  </svg>
                </div>
                <div className="slider-track-container">
                  <div 
                    className="slider-track-fill" 
                    style={{ 
                      width: selectedTrackingTrip.status === 'delivered' ? '100%' :
                             selectedTrackingTrip.status === 'in_transit' ? '70%' : '15%'
                    }} 
                  />
                </div>
                <div className={`slider-right-endpoint ${selectedTrackingTrip.status === 'delivered' ? 'completed' : ''}`}>
                  <div className="inner-dot" />
                </div>
              </div>

              <div className="dark-card-footer-row">
                <div className="footer-col-left">
                  <span className="time-stamp">12 Aug 24 15:34</span>
                  <span className="address-name">Ileapa Secondary School</span>
                </div>
                <div className="footer-col-right">
                  <span className="time-stamp-label">Estimated: 12 Aug 24 15:34</span>
                  <span className="address-name-right">Ileapa Secondary School</span>
                </div>
              </div>
            </div>

            {/* Courier details card */}
            {assignedCourier && (
              <div className="details-courier-card">
                <div className="courier-avatar-container">
                  {assignedCourier.photoUrl ? (
                    <img src={assignedCourier.photoUrl} alt={assignedCourier.name} className="courier-avatar" />
                  ) : (
                    <div className="courier-avatar-placeholder">👤</div>
                  )}
                </div>
                <div className="courier-info">
                  <div className="courier-name-rating">
                    <span className="courier-name">{assignedCourier.name}</span>
                    <div className="courier-rating">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="2" style={{ marginRight: '2px' }}>
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                      </svg>
                      <span>{assignedCourier.rating.toFixed(1)}</span>
                    </div>
                  </div>
                  <span className="courier-vehicle-model">
                    {`${assignedCourier.vehicle.color} ${assignedCourier.vehicle.make} ${assignedCourier.vehicle.model}`}
                  </span>
                  <span className="courier-vehicle-plate">
                    {assignedCourier.vehicle.licensePlate}
                  </span>
                </div>
                {selectedTrackingTrip.status !== 'delivered' && selectedTrackingTrip.status !== 'failed' && (
                  <div className="courier-actions">
                    <button className="courier-action-btn chat" onClick={() => setIsChatOpen(true)} aria-label="Chat with Courier">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                      </svg>
                    </button>
                    <a href={`tel:${assignedCourier.phone}`} className="courier-action-btn call" aria-label="Call Courier">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                      </svg>
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Delivery Information white card */}
            <div className="details-info-card">
              <h3 className="info-title">Delivery Information</h3>
              <hr className="details-divider" />
              
              <div className="info-row-sender-recipient">
                <div className="info-sender">
                  <span className="info-label">Sender</span>
                  <span className="info-name">Howard Webb</span>
                  <span className="info-phone">+2348172839281</span>
                </div>
                <div className="info-recipient">
                  <span className="info-label">Recipient</span>
                  <span className="info-name">Nicolas Greene</span>
                  <span className="info-phone">+2348172839281</span>
                </div>
              </div>
              
              <hr className="details-divider" />
              
              <div className="info-row-route">
                <div className="route-from">
                  <span className="info-label">From</span>
                  <span className="info-val-large">Home</span>
                </div>
                <div className="route-connector-dots">
                  <span>&middot;&middot;&middot;&middot;&middot;&middot;&middot;&middot;</span>
                </div>
                <div className="route-to">
                  <span className="info-label">Destination</span>
                  <span className="info-val-large">Hospital</span>
                </div>
              </div>
              
              <div className="details-fields-list">
                <div className="fields-item">
                  <span className="field-label">Item name</span>
                  <span className="field-value-bold">{selectedTrackingTrip.title || 'Nike Sneakers'}</span>
                </div>
                <div className="fields-item">
                  <span className="field-label">Item type</span>
                  <span className="field-value-bold flex-align-center">
                    <svg style={{ marginRight: '6px', color: '#64748b' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23z"></path>
                    </svg>
                    {selectedTrackingTrip.type ? selectedTrackingTrip.type.charAt(0).toUpperCase() + selectedTrackingTrip.type.slice(1) + ' and apparels' : 'Clothing and apparels'}
                  </span>
                </div>
                <div className="fields-item">
                  <span className="field-label">Delivery type</span>
                  <span className="field-value-bold">Same day</span>
                </div>
                <div className="fields-item">
                  <span className="field-label">Size of item</span>
                  <div className="field-value-stacked">
                    <span className="field-value-bold">20x10x15cm</span>
                    <span className="field-value-sub">&lt;1kg</span>
                  </div>
                </div>
                <div className="fields-item">
                  <span className="field-label">Amount</span>
                  <span className="field-value-bold">NGN5,000</span>
                </div>
              </div>
            </div>

            {/* Quick Actions / Report inside details */}
            <div style={{ marginTop: 'auto', paddingTop: '12px' }}>
              <button 
                className="custom-submit-report-btn" 
                style={{ width: '100%', padding: '12px', background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: '12px', fontWeight: '700', cursor: 'pointer' }}
                onClick={() => setIsReportModalOpen(true)}
              >
                Report Delivery Issue
              </button>
            </div>
          </div>
        )}

        {/* Chat Component rendering side-by-side next to the details panel */}
        {isChatOpen && assignedCourier && selectedTrackingTrip?.id && (
          <Chat
            tripId={selectedTrackingTrip.id}
            user={auth.currentUser}
            participant={{
              id: selectedTrackingTrip.rawTrip?.driverId || 'courier',
              name: assignedCourier.name,
              photoUrl: assignedCourier.photoUrl
            }}
            onClose={() => setIsChatOpen(false)}
          />
        )}

        {/* Right side map - matching Go.jsx page layout */}
        <div className="packagedelivery-map-container" ref={mapRef}></div>
        <div className="map-zoom-controls">
          <button onClick={handleZoomIn} aria-label="Zoom in">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
          </button>
          <button onClick={handleZoomOut} aria-label="Zoom out">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
          </button>
        </div>
      </div>

      {/* Check Pricing Modal */}
      {isPricingModalOpen && (
        <div className="packagedelivery-modal-overlay" onClick={() => setIsPricingModalOpen(false)}>
          <div className="packagedelivery-modal" onClick={(e) => e.stopPropagation()}>
            <div className="packagedelivery-modal-header">
              <h2>9trodelivery Pricing</h2>
              <button className="packagedelivery-modal-close-btn" onClick={() => setIsPricingModalOpen(false)}>✕</button>
            </div>
            <div className="packagedelivery-modal-body" style={{ padding: '20px 0' }}>
              <div className="custom-details-section" style={{ border: 'none', padding: 0 }}>
                <div className="detail-item" style={{ padding: '12px 0', borderBottom: '1px solid #edf2f7' }}>
                  <span className="detail-label" style={{ fontWeight: '600' }}>Motorcycle Courier</span>
                  <span className="detail-value">₦400 base + ₦120/km</span>
                </div>
                <div className="detail-item" style={{ padding: '12px 0', borderBottom: '1px solid #edf2f7' }}>
                  <span className="detail-label" style={{ fontWeight: '600' }}>Express Cargo Truck</span>
                  <span className="detail-value">₦1,500 base + ₦300/km</span>
                </div>
                <div className="detail-item" style={{ padding: '12px 0' }}>
                  <span className="detail-label" style={{ fontWeight: '600' }}>Eco Bicycle Courier</span>
                  <span className="detail-value">₦300 base + ₦80/km</span>
                </div>
              </div>
            </div>
            <div className="packagedelivery-modal-footer">
              <button className="cancel-trip-btn" onClick={() => setIsPricingModalOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Report Incident Modal */}
      {isReportModalOpen && (
        <div className="packagedelivery-modal-overlay" onClick={() => setIsReportModalOpen(false)}>
          <div className="packagedelivery-modal" onClick={(e) => e.stopPropagation()}>
            <button className="packagedelivery-modal-close-btn" style={{ position: 'absolute', right: '16px', top: '16px' }} onClick={() => setIsReportModalOpen(false)} aria-label="Close modal">
              ✕
            </button>
            <div className="packagedelivery-modal-header">
              <h2>Report Delivery Issue</h2>
            </div>
            
            {reportSuccess ? (
              <div className="custom-report-success-state" style={{ padding: '30px 24px', textAlign: 'center' }}>
                <div className="success-icon-ring" style={{ margin: '0 auto 16px auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                <h3>Report Submitted</h3>
                <p>Thank you. Your feedback helps keep the 9tro community safe. We will investigate this incident.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmitReport} className="custom-report-form" style={{ padding: '24px' }}>
                <p className="form-sub-label" style={{ marginBottom: '16px' }}>Select the reason for reporting this delivery:</p>
                <div className="reason-options" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
                  {[
                    'Damaged Package',
                    'Delayed Delivery',
                    'Courier Conduct',
                    'Missing Items',
                    'Safety Concerns'
                  ].map((reason) => (
                    <label key={reason} className={`reason-radio-option ${reportReason === reason ? 'selected' : ''}`} style={{ display: 'flex', gap: '10px', alignItems: 'center', cursor: 'pointer' }}>
                      <input 
                        type="radio" 
                        name="reportReason" 
                        value={reason} 
                        checked={reportReason === reason} 
                        onChange={(e) => setReportReason(e.target.value)} 
                      />
                      <span>{reason}</span>
                    </label>
                  ))}
                </div>

                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
                  <label htmlFor="reportComments" style={{ fontWeight: '600' }}>Additional Details:</label>
                  <textarea 
                    id="reportComments" 
                    rows="3" 
                    value={reportComments} 
                    onChange={(e) => setReportComments(e.target.value)} 
                    placeholder="Describe what happened..." 
                    style={{ width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '12px', boxSizing: 'border-box' }}
                    required 
                  />
                </div>

                <div className="custom-modal-footer-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button type="button" className="custom-cancel-btn" style={{ padding: '8px 16px', background: '#f1f5f9', border: 'none', borderRadius: '8px', cursor: 'pointer' }} onClick={() => setIsReportModalOpen(false)}>Cancel</button>
                  <button type="submit" className="custom-submit-report-btn" style={{ padding: '8px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }} disabled={isReporting}>
                    {isReporting ? 'Submitting...' : 'Submit Report'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

export default PackageDelivery;
